import PropTypes from "prop-types";
import "./AccountsTable.css";
import { formatCurrency, parseCurrencyAmount } from "../../utils/utils";
import { useThresholdEditor } from "../../hooks/useThresholdEditor";
import ThresholdEditCell from "./ThresholdEditCell";

const StatusPill = ({ status }) => {
  const getStatusClass = () => {
    switch (status) {
      case "Critical":
        return "status-pill--critical";
      case "Close to Threshold":
        return "status-pill--warning";
      case "Safe":
        return "status-pill--safe";
      default:
        return "";
    }
  };

  return <span className={`status-pill ${getStatusClass()}`}>{status}</span>;
};

StatusPill.propTypes = {
  status: PropTypes.string.isRequired,
};

const AccountsTable = ({
  accounts,
  onAccountSelect,
  selectedAccountIndex,
  onThresholdUpdate,
}) => {
  const {
    editingIndex,
    editValue,
    saving,
    startEditing,
    cancelEditing,
    updateEditValue,
    saveThreshold,
  } = useThresholdEditor(accounts, onThresholdUpdate);

  const calculateStatus = (availableBalance, threshold) => {
    const available = parseCurrencyAmount(availableBalance);
    const thresholdValue = parseCurrencyAmount(threshold);

    if (thresholdValue === 0) {
      return "Safe"; // If threshold is 0, consider it safe
    }

    const percentage = (available / thresholdValue) * 100;

    if (percentage > 90) {
      return "Safe";
    } else if (percentage >= 50) {
      return "Close to Threshold";
    } else {
      return "Critical";
    }
  };

  const handleEditThreshold = (e, index, currentThreshold) => {
    e.stopPropagation(); // Prevent row click
    startEditing(index, currentThreshold);
  };

  const handleSaveThreshold = async (e, account) => {
    e.stopPropagation(); // Prevent row click
    await saveThreshold(account);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation(); // Prevent row click
    cancelEditing();
  };

  return (
    <div className="accounts-table-container">
      <table className="accounts-table" role="table" aria-label="Currency accounts table">
        <thead>
          <tr>
            <th>
              Name
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5L6 1.5L9 4.5"
                  stroke="#718096"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 7.5L6 10.5L9 7.5"
                  stroke="#718096"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </th>
            <th>Currency</th>
            <th>Due Bookings (Next 7 days)</th>
            <th>Threshold</th>
            <th>Available Balance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account, index) => {
            const calculatedStatus = calculateStatus(
              account.available_amount || account.availableBalance,
              account.threshold
            );
            const isEditing = editingIndex === index;

            return (
              <tr
                key={index}
                className={
                  selectedAccountIndex === index ? "account-row--selected" : ""
                }
                onClick={() =>
                  onAccountSelect && onAccountSelect(account, index)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && onAccountSelect) {
                    e.preventDefault();
                    onAccountSelect(account, index);
                  }
                }}
                tabIndex={onAccountSelect ? 0 : -1}
                role="row"
                aria-selected={selectedAccountIndex === index}
                style={{ cursor: onAccountSelect ? "pointer" : "default" }}
              >
                <td className="account-name">
                  Hotel Trader {account.currency} Account
                </td>
                <td>{account.currency}</td>
                <td>{formatCurrency(account.due_7_days, account.currency)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <ThresholdEditCell
                    isEditing={isEditing}
                    editValue={editValue}
                    saving={saving}
                    threshold={account.threshold}
                    currency={account.currency}
                    index={index}
                    onEdit={(e) => handleEditThreshold(e, index, account.threshold)}
                    onSave={(e) => handleSaveThreshold(e, account)}
                    onCancel={handleCancelEdit}
                    onChange={updateEditValue}
                  />
                </td>
                <td
                  className={
                    calculatedStatus === "Critical"
                      ? "available-balance--critical"
                      : ""
                  }
                >
                  {formatCurrency(account.available_amount, account.currency)}
                </td>
                <td>
                  <StatusPill status={calculatedStatus} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

AccountsTable.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      currency: PropTypes.string.isRequired,
      due_7_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      threshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      available_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  onAccountSelect: PropTypes.func,
  selectedAccountIndex: PropTypes.number,
  onThresholdUpdate: PropTypes.func,
};

export default AccountsTable;
